export type PlanCode = "FREE" | "PRO" | "ENTERPRISE";

export type Plan = {
  code: PlanCode;
  name: string;
  description?: string;
  monthlyPricePaise: number;
  annualPricePaise: number;
  annualDiscountPercent: number;
  interval: "month" | "annual" | "lifetime" | "custom";
  monthlyCreditGrant: number;   // 0 for FREE
  features: string[];
  hidden?: boolean; // hidden plans kept for backward compat but excluded from UI
  dailyGenerationLimit: number | null; // null = unlimited
  isPopular?: boolean;
};

export const PLANS: Record<PlanCode, Omit<Plan, 'code'>> = {
  FREE: {
    name: "Free",
    description: "Get started with AI-assisted writing at no cost.",
    monthlyPricePaise: 0,
    annualPricePaise: 0,
    annualDiscountPercent: 0,
    interval: "lifetime",
    monthlyCreditGrant: 10_000,
    dailyGenerationLimit: 5,
    features: [
      "5 AI generations per day",
      "1 LLM model (Gemini Flash)",
      "Basic story templates",
      "Community support",
    ],
  },
  PRO: {
    name: "Pro Author",
    description: "Unlimited writing power for serious authors.",
    monthlyPricePaise: 29_900,      // ₹299/mo
    annualPricePaise: 299_900,      // ₹2,999/yr (~16% discount)
    annualDiscountPercent: 16,
    interval: "month",
    monthlyCreditGrant: 100_000,
    dailyGenerationLimit: null,
    isPopular: true,
    features: [
      "Unlimited AI generations",
      "Claude, Gemini & OpenAI LLMs",
      "Collaboration tools",
      "Export to PDF & DOCX",
      "Advanced story blueprints & outlines",
      "Priority support",
    ],
  },
  ENTERPRISE: {
    name: "Enterprise",
    description: "Scalable AI writing for teams and publishers.",
    monthlyPricePaise: 99_900,      // ₹999/mo
    annualPricePaise: 999_900,      // ₹9,999/yr (~17% discount)
    annualDiscountPercent: 17,
    interval: "month",
    monthlyCreditGrant: 500_000,
    dailyGenerationLimit: null,
    features: [
      "Everything in Pro",
      "Team workspaces (up to 10 members)",
      "API access for custom integrations",
      "Custom LLM configurations",
      "Dedicated account manager",
    ],
  },
};

// Helper to convert PLANS record to an array of Plan objects
export const getPlansArray = (): Plan[] => {
  return Object.entries(PLANS).map(([code, plan]) => ({
    code: code as PlanCode,
    ...plan,
  }));
};

// Returns only plans visible to new users (excludes hidden legacy tiers)
export const getVisiblePlans = (): Plan[] => {
  return getPlansArray().filter(p => !p.hidden);
};
